import { css } from '@emotion/react';
import { LinkAuthenticationElement } from '@stripe/react-stripe-js';
import { useSearch } from '@tanstack/react-location';
import { PaymentMethodTypeEnum } from '@frontend/api-weave-pay';
import { formatDisplayAmount } from '@frontend/currency';
import { useTranslation } from '@frontend/i18n';
import { FormRow, MoneyField, TextField, TextLink } from '@frontend/design-system';
import { usePortalPayment } from '../hooks';
import { usePatientDetailsForm } from './hooks';

/**
 * Must be wrapped by Stripe Elements component
 * @param requestedAmount - Requested amount in dollars
 * @param amountEditable - If the requested amount can be edited
 * @param paymentSuccess - Success of payment
 */

type AffirmRedirectParams = {
  payment_intent_client_secret?: string;
  payment_intent?: string;
  amount?: number;
  receiptEmail?: string;
  firstName?: string;
  lastName?: string;
};

interface PatientDetailsFormProps {
  requestedAmount?: number;
  amountEditable?: boolean;
  paymentSuccess?: boolean;
}

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
  `,
};

export const PatientDetailsForm = ({
  requestedAmount,
  amountEditable = true,
  paymentSuccess,
}: PatientDetailsFormProps) => {
  const { t } = useTranslation('weave-pay');
  const { payment, setPayment } = usePortalPayment();
  const params: AffirmRedirectParams = useSearch();

  // TODO:
  // Flesh out resetSuccessPayment
  const resetSuccessPayment = () => true;
  const { getFieldProps, values, seedValues } = usePatientDetailsForm({ requestedAmount });

  return (
    <div css={styles.container}>
      <LinkAuthenticationElement
        onFocus={() => resetSuccessPayment()}
        onChange={(event) => {
          setPayment({
            ...payment,
            receiptEmail: event.value.email,
            paymentMethodType: PaymentMethodTypeEnum.LINK,
          });
        }}
        options={{ defaultValues: { email: params?.receiptEmail || '' } }}
      />
      <FormRow>
        <TextField
          className='firstName'
          label={t('Patient First Name*')}
          {...getFieldProps('firstName')}
          hasNonFloatingLabel
          readOnly={paymentSuccess}
        />
        <TextField
          label={t('Last Name*')}
          {...getFieldProps('lastName')}
          hasNonFloatingLabel
          readOnly={paymentSuccess}
        />
      </FormRow>
      {amountEditable && (
        <FormRow>
          <MoneyField
            label={t('Payment Amount*')}
            css={{ width: '100%' }}
            {...getFieldProps('amount')}
            hasNonFloatingLabel
            disabled={paymentSuccess}
          />
          {!paymentSuccess && requestedAmount && Number(values.amount) !== requestedAmount && (
            <TextLink onClick={() => seedValues({ amount: formatDisplayAmount(requestedAmount) })}>
              {t('Reset to full amount')}
            </TextLink>
          )}
        </FormRow>
      )}
    </div>
  );
};
